import { call, select } from 'redux-saga/effects';
import config from '../../../config';
import { RequestError } from '../../../utils/request';
import { addIncompleteSurvey, removeIncompleteSurveyByFeedbackId } from '../utils/incompleteSurveys';
import { getFlowSubmissionSession } from '../utils/submissionSession';
import { prepareFeedbackSubmission } from '../utils/prepareFeedbackSubmission';
import { submitWithForegroundCaptcha } from './submissionCaptchaSaga';

/**
 * Submits a captured feedback context using the caller's answering session.
 * The session owns incomplete identity/version and chooses its path before captcha.
 * It returns a distinct queued result, never a fabricated HTTP response.
 */
export function* submitFeedbackSaga({
  session,
  feedback,
  revision,
  isIncomplete = false,
  feedbackId = null,
  version = null,
  getCaptchaToken,
  signal,
}) {
  const state = yield select();

  if (state.flow.isPreview || state.flow.isSingleElementPreview) return { status: 'skipped', reason: 'preview' };

  if (isIncomplete && state.flow.allowSavingIncompleteSurvey === false) {
    return { status: 'skipped', reason: 'incomplete_disabled' };
  }

  session = session || getFlowSubmissionSession(state.flow, state.node.id);
  const apiUrl = config.API_URL;
  const isOnline = globalThis.navigator?.onLine !== false;

  const input = prepareFeedbackSubmission({
    feedback,
    apiUrl,
    submissionId: session.submissionId,
    revision: revision ?? session.nextRevision(),
    isIncomplete,
    feedbackId,
    version,
    search: state.route?.location?.search ?? globalThis.location?.search ?? '',
    launchParams: state.flow.launchContext?.params,
    isWidget: Boolean(state.widget),
    isOnline,
    captcha: {
      required: config.CAPTCHA_ENABLED !== false && !!state.node.requireFeedbackCaptcha,
      provider: state.node.captchaProvider,
    },
  });

  let result;

  try {
    result = yield call(
      submitWithForegroundCaptcha,
      session,
      input,
      { apiUrl, isOnline, getCaptchaToken, signal },
      state,
    );
  } catch (error) {
    if (isOnline) throw error;

    // The offline route only persists locally, so its failures mean nothing was saved.
    const storageError = new RequestError('storage', 'Offline submission could not be saved');
    storageError.cause = error;

    throw storageError;
  }
  const current = yield select();

  // Old Flow completions cannot replace the identity of a reset/reloaded session.
  if (
    current.flow.submissionContext === state.flow.submissionContext &&
    ['submitted', 'queued'].includes(result.status)
  ) {
    try {
      if (isIncomplete && !session.getState().finalized) {
        addIncompleteSurvey(result.feedbackId, input.nodeId, input.flowId, result.version);
      } else if (!isIncomplete && result.feedbackId) {
        removeIncompleteSurveyByFeedbackId(result.feedbackId);
      }
    } catch (error) {
      // A local identity-cache failure cannot invalidate an accepted submission.
      result.cleanupErrors = [...(result.cleanupErrors || []), { operation: 'identity', name: error.name }];
    }
  }

  return result;
}
